import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { trackEvent } from "../../../../utils/analytics";
import { formatPtoTimeRange, getPtoRequestedHours, getStaffingDate, isFullStaffingDayPto } from "../../../../utils/dateUtils";
import type { DayOff, Nurse } from "../../../../utils/models";
import { daysOffAndPtoToNursePreferences, nursePreferencesToDaysOffAndPto } from "../../../../utils/utilFunctions";
import { useEditDayOff } from "./useEditDayOff";

type Props = {
	nurse: Nurse | undefined;
	setNurseData?: Dispatch<SetStateAction<Nurse>>;
	onDeletePtoConfirmed?: (dayOff: DayOff, restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => Promise<void>;
};

export const useDaysOffAndPtoState = ({ setNurseData, nurse, onDeletePtoConfirmed }: Props) => {
	const [addPtoOpen, setAddPtoOpen] = useState(false);
	const [dayOffToDelete, setDayOffToDelete] = useState<DayOff | null>(null);
	const [dayOffToEdit, setDayOffToEdit] = useState<DayOff | null>(null);
	const { updateNurse, getNurseById, chosenDepartment } = useHospitalData();

	// Use the nurse from hospital data context if available, so we pick up updates
	// from other components (e.g., PTO added via Weekly Schedule's NurseMenu)
	const currentNurse = useMemo(() => {
		if (!nurse?.id) return nurse;
		const updatedNurse = getNurseById(nurse.id);
		return updatedNurse ?? nurse;
	}, [nurse, getNurseById]);

	const updateHospitalDataOnSave = useCallback(
		(newDayOff: DayOff, operation: "delete" | "add", relevantNurseId?: number) => {
			const nurseToUse = nurse ?? (relevantNurseId !== undefined ? getNurseById(relevantNurseId) : undefined);
			if (nurseToUse) {
				if (operation === "add") {
					updateNurse({
						...nurseToUse,
						employeeSpecificPreferences: [...nurseToUse.employeeSpecificPreferences, ...daysOffAndPtoToNursePreferences([newDayOff], nurseToUse)],
					});
				} else if (operation === "delete") {
					updateNurse({
						...nurseToUse,
						employeeSpecificPreferences: nurseToUse.employeeSpecificPreferences.filter((pref) => pref.id !== newDayOff.id),
					});
				}
			}
		},
		[nurse, updateNurse, getNurseById],
	);

	const toggleAddPtoOpen = useCallback(() => {
		setAddPtoOpen((prev) => !prev);
		trackEvent("profile_add_day_off_clicked", {
			nurse_id: nurse?.id,
		});
	}, [nurse?.id]);

	const closeAddPtoOpen = useCallback(() => {
		toggleAddPtoOpen();
		trackEvent("add_day_off_cancelled", {
			nurse_id: nurse?.id,
		});
	}, [toggleAddPtoOpen, nurse?.id]);

	const daysOff = useMemo(() => {
		return nursePreferencesToDaysOffAndPto(currentNurse);
	}, [currentNurse]);

	const handleSaveNewDayOff = useCallback(
		(newDayOff: DayOff) => {
			setNurseData?.((prevNurse) => ({
				...prevNurse,
				employeeSpecificPreferences: daysOffAndPtoToNursePreferences([...daysOff, newDayOff], prevNurse),
			}));
			updateHospitalDataOnSave(newDayOff, "add");
			trackEvent("day_off_saved", {
				nurse_id: nurse?.id,
				date_from: newDayOff.dateFrom?.toFormat("yyyy-MM-dd"),
				date_to: newDayOff.dateTo?.toFormat("yyyy-MM-dd"),
			});
		},
		[daysOff, nurse, setNurseData, updateHospitalDataOnSave],
	);

	const handleDeleteClick = useCallback((dayOff: DayOff) => {
		setDayOffToDelete(dayOff);
	}, []);
	const handleDeleteCancel = useCallback(() => {
		setDayOffToDelete(null);
	}, []);
	const handleDeleteConfirm = useCallback(
		async (restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => {
			if (dayOffToDelete) {
				setDayOffToDelete(null);
				await onDeletePtoConfirmed?.(dayOffToDelete, restoreRecurringShifts, restoreTrimmedShifts);
				updateHospitalDataOnSave(dayOffToDelete, "delete");
				setNurseData?.((prevNurse) => ({
					...prevNurse,
					employeeSpecificPreferences: prevNurse.employeeSpecificPreferences.filter((pref) => pref.id !== dayOffToDelete.id),
				}));
			}
		},
		[dayOffToDelete, onDeletePtoConfirmed, updateHospitalDataOnSave, setNurseData],
	);

	const handleEditClick = useCallback((dayOff: DayOff | null) => setDayOffToEdit(dayOff), []);

	const { handleEditSave } = useEditDayOff({
		nurse,
		setNurseData,
		onSaved: () => setDayOffToEdit(null),
	});

	const formatPtoDateRange = useCallback(
		(dayOff: DayOff): string => {
			// Exact-times (partial-day) PTO shows its window next to the date (DAT-230).
			if (!isFullStaffingDayPto(dayOff.dateFrom, dayOff.dateTo, chosenDepartment.dayStart)) {
				const staffingDay = getStaffingDate(dayOff.dateFrom, chosenDepartment.dayStart);
				return `${staffingDay.toFormat("ccc, MMM d, yyyy")}, ${formatPtoTimeRange(dayOff.dateFrom, dayOff.dateTo)}`;
			}

			const start = getStaffingDate(dayOff.dateFrom, chosenDepartment.dayStart);
			const end = getStaffingDate(dayOff.dateTo.minus({ minutes: 1 }), chosenDepartment.dayStart);

			return start.hasSame(end, "day") ? start.toFormat("ccc, MMM d, yyyy") : `${start.toFormat("ccc, MMM d, yyyy")} - ${end.toFormat("ccc, MMM d, yyyy")}`;
		},
		[chosenDepartment.dayStart],
	);

	const getRequestedHours = useCallback(
		(dayOff: DayOff): number =>
			getPtoRequestedHours(
				dayOff.dateFrom.setZone(chosenDepartment.timezone),
				dayOff.dateTo.setZone(chosenDepartment.timezone),
				dayOff.hours,
				chosenDepartment.dayStart,
			),
		[chosenDepartment.timezone, chosenDepartment.dayStart],
	);

	return {
		addPtoOpen,
		toggleAddPtoOpen,
		closeAddPtoOpen,
		handleSaveNewDayOff,
		daysOff,
		dayOffToDelete,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		dayOffToEdit,
		handleEditClick,
		handleEditSave,
		formatPtoDateRange,
		getRequestedHours,
	};
};
